import React from 'react'

// ── Design tokens ─────────────────────────────────────────────────────────────
export const C = {
  blue:    '#2D9EFF',
  blueDim: '#1A7FE0',
  blueHi:  '#7DCEFF',
  blueBg:  '#2D9EFF28',
  blueBdr: '#2D9EFF55',
  green:   '#00F0A0',
  red:     '#FF4466',
  yellow:  '#FFD000',
  bk:      '#080B18',
  bk2:     '#0D1225',
  bk3:     '#121830',
  bk4:     '#18203C',
  border:  '#243058',
  text:    '#F0F4FF',
  dim:     '#7A8DB8',
  dim2:    '#1E2A4A',
}

// ── Avatar ────────────────────────────────────────────────────────────────────
const AVA_COLORS = [
  [C.blueBg,    C.blueHi],
  ['#00E89620', C.green],
  ['#FFBA0020', C.yellow],
  ['#FF335520', C.red],
  ['#1A8FFF18', C.blue],
  ['#60BBFF18', C.blueHi],
]

export function Avatar({ name = '', idx = 0, size = 40 }) {
  const [bg, fg] = AVA_COLORS[idx % AVA_COLORS.length]
  const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: bg, color: fg,
      border: `1px solid ${fg}30`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Rajdhani', sans-serif", fontWeight: 700,
      fontSize: size * 0.3, flexShrink: 0,
    }}>
      {initials}
    </div>
  )
}

// ── Badge ─────────────────────────────────────────────────────────────────────
const BADGE_STYLES = {
  blue:   { bg: C.blueBg,    color: C.blue,   border: C.blueBdr },
  green:  { bg: '#00E89618', color: C.green,  border: '#00E89635' },
  yellow: { bg: '#FFBA0018', color: C.yellow, border: '#FFBA0035' },
  red:    { bg: '#FF335518', color: C.red,    border: '#FF335535' },
}

export function Badge({ type = 'blue', children }) {
  const s = BADGE_STYLES[type] || BADGE_STYLES.blue
  return (
    <span style={{
      background: s.bg, color: s.color,
      border: `1px solid ${s.border}`,
      borderRadius: 5, padding: '2px 7px',
      fontSize: 9.5, fontWeight: 700,
      fontFamily: "'Rajdhani', sans-serif",
      letterSpacing: '0.03em', whiteSpace: 'nowrap',
    }}>
      {children}
    </span>
  )
}

// ── Progress bar ──────────────────────────────────────────────────────────────
export function Track({ pct = 0, color = C.blue, height = 5, shimmer = false }) {
  return (
    <div style={{ height, background: C.dim2, borderRadius: 3, overflow: 'hidden', width: '100%' }}>
      <div style={{
        height: '100%', borderRadius: 3,
        width: `${pct}%`,
        background: shimmer
          ? `linear-gradient(90deg, ${C.blueDim} 0%, ${C.blue} 40%, ${C.blueHi} 60%, ${C.blue} 80%, ${C.blueDim} 100%)`
          : `linear-gradient(90deg, ${C.blueDim} 0%, ${color} 100%)`,
        backgroundSize: shimmer ? '200% auto' : undefined,
        animation: shimmer ? 'shimmer 2s linear infinite' : 'fillBar 0.9s cubic-bezier(.4,0,.2,1) forwards',
        '--w': `${pct}%`,
        transition: shimmer ? undefined : undefined,
      }} />
    </div>
  )
}

// ── Live dot ──────────────────────────────────────────────────────────────────
export function LiveDot({ size = 7 }) {
  return (
    <span style={{
      display: 'inline-block',
      width: size, height: size,
      borderRadius: '50%',
      background: C.green,
      boxShadow: `0 0 6px ${C.green}`,
      animation: 'blink 1.1s ease-in-out infinite',
    }} />
  )
}

// ── Stat value colored by direction ──────────────────────────────────────────
export function StatVal({ val, suffix = '%', positive = true }) {
  const color = positive ? C.green : C.red
  return (
    <span style={{ color, fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600 }}>
      {val}{suffix}
    </span>
  )
}

// ── Card ──────────────────────────────────────────────────────────────────────
export function Card({ children, style = {}, glow = false }) {
  return (
    <div style={{
      background: glow
        ? `linear-gradient(135deg, ${C.bk3} 0%, ${C.bk2} 100%)`
        : C.bk2,
      border: `1px solid ${glow ? C.blueBdr : C.border}`,
      borderRadius: 16,
      overflow: 'hidden',
      ...(glow ? { boxShadow: `0 0 24px ${C.blue}18` } : {}),
      ...style,
    }}>
      {children}
    </div>
  )
}

// ── Section label ─────────────────────────────────────────────────────────────
export function SectionLabel({ left, right }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '14px 16px 6px',
      fontFamily: "'Rajdhani', sans-serif", fontWeight: 700,
      fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase',
      color: C.dim,
    }}>
      <span>{left}</span>
      {right && <span style={{ color: C.blue, fontSize: 10 }}>{right}</span>}
    </div>
  )
}

// ── Loading spinner ───────────────────────────────────────────────────────────
export function Spinner() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
      <div style={{
        width: 32, height: 32, borderRadius: '50%',
        border: `3px solid ${C.border}`,
        borderTopColor: C.blue,
        animation: 'spin 0.8s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

// ── Empty state ───────────────────────────────────────────────────────────────
export function Empty({ message = 'No data available' }) {
  return (
    <div style={{ textAlign: 'center', padding: '40px 16px', color: C.dim }}>
      <div style={{ fontSize: 28, marginBottom: 8 }}>◆</div>
      <div style={{ fontSize: 13, fontFamily: "'Rajdhani', sans-serif", fontWeight: 600 }}>{message}</div>
    </div>
  )
}

// ── Mono number ───────────────────────────────────────────────────────────────
export function Mono({ children, size = 13, color = C.text, bold = false }) {
  return (
    <span style={{
      fontFamily: "'IBM Plex Mono', monospace",
      fontSize: size, color, fontWeight: bold ? 700 : 400,
    }}>
      {children}
    </span>
  )
}

// ── Form trend ────────────────────────────────────────────────────────────────
// ▲ heating up / ▼ cooling down — last-7-game HRR rate vs the hitter's baseline.
// Renders nothing when flat or unknown. `size` scales the glyph + %.
export function FormTrend({ trend, delta, size = 9 }) {
  if (!trend || trend === 'flat') return null
  const up = trend === 'up'
  const col = up ? C.green : C.red
  const pct = delta != null ? `${Math.abs(delta * 100).toFixed(0)}%` : ''
  return (
    <span
      title={`Form ${up ? 'up' : 'down'} ${pct} vs baseline (last 7 games)`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 1, color: col,
        fontSize: size, fontWeight: 800, fontFamily: "'Rajdhani', sans-serif", flexShrink: 0,
      }}>
      {up ? '▲' : '▼'}{pct}
    </span>
  )
}

// ── Ticker ────────────────────────────────────────────────────────────────────
export function Ticker({ items = [] }) {
  const doubled = [...items, ...items]
  return (
    <div style={{ overflow: 'hidden', background: C.bk2, borderBottom: `1px solid ${C.border}`, padding: '7px 0' }}>
      <div style={{ display: 'inline-block', animation: 'ticker 30s linear infinite', whiteSpace: 'nowrap' }}>
        {doubled.map((item, i) => (
          <span key={i} style={{
            display: 'inline-block', marginRight: 32,
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
            color: i % 2 === 0 ? C.blue : C.dim,
          }}>
            ◆ {item}
          </span>
        ))}
      </div>
    </div>
  )
}
